import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import moment from "moment";
import {
  loadFiltersFromStorage,
  saveFiltersToStorage,
  clearFiltersFromStorage,
} from "../utils/filterStorage";
import {
  getOfficeNavigationContext,
  isInOfficeNavigationContext,
} from "utils/officeNavigationContext";
import { sortingVariables } from "../StaffTrackingTable";
import { useAccountOwnerCommon } from "lib/Context/AccountOwner";

export const useStaffTrackingFilter = (activeTab) => {
  // Filter states (temporary - for UI)
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [statuses, setStatuses] = useState([]);
  const [selectedEmpFilter, setSelectedEmpFilter] = useState([]);
  const [selectedOfficeFilter, setSelectedOfficeFilter] = useState([]);
  const [selectedRoles, setSelectedRoles] = useState([]);
  const [searchValue, setSearchValue] = useState("");
  const [searchValueApplied, setSearchValueApplied] = useState("");
  const [radioChecked, setRadioChecked] = useState(false);
  const { effectiveOwnerId } = useAccountOwnerCommon();
  const preservedRef = useRef(null);
  // Applied filter states (used for API calls)
  const [startDateApplied, setStartDateApplied] = useState(null);
  const [endDateApplied, setEndDateApplied] = useState(null);
  const [statusesApplied, setStatusesApplied] = useState([]);
  const [selectedEmpFilterApplied, setSelectedEmpFilterApplied] = useState([]);
  const [selectedOfficeFilterApplied, setSelectedOfficeFilterApplied] =
    useState([]);
  const [radioCheckedApplied, setRadioCheckedApplied] = useState(false);

  // Sorting
  const [sortBy, setSortBy] = useState(sortingVariables.UserName);
  const [isDescending, setIsDescending] = useState(true);

  // Reseting filter state
  const [resetSearchTrigger, setResetSearchTrigger] = useState(0);
  const isFilterApplied = useMemo(() => {
    return (
      statusesApplied.length > 0 ||
      (activeTab === "1" && selectedEmpFilterApplied.length > 0) ||
      (activeTab === "2" && selectedOfficeFilterApplied.length > 0) ||
      startDateApplied !== null ||
      endDateApplied !== null ||
      radioCheckedApplied
    );
  }, [
    statusesApplied,
    selectedEmpFilterApplied,
    selectedOfficeFilterApplied,
    startDateApplied,
    endDateApplied,
    activeTab,
    radioCheckedApplied,
  ]);
  // Load filters from localStorage on mount and when tab changes
  useEffect(() => {
    const savedFilters = loadFiltersFromStorage(activeTab);
    if (savedFilters) {
      if (savedFilters.startDateApplied) {
        const date = new Date(savedFilters.startDateApplied);
        setStartDateApplied(savedFilters.startDateApplied);
        setStartDate(date);
      } else {
        setStartDate(null);
        setStartDateApplied(null);
      }
      if (savedFilters.endDateApplied) {
        const date = new Date(savedFilters.endDateApplied);
        setEndDateApplied(savedFilters.endDateApplied);
        setEndDate(date);
      } else {
        setEndDate(null);
        setEndDateApplied(null);
      }
      if (savedFilters.statusesApplied) {
        setStatuses(savedFilters.statusesApplied);
        setStatusesApplied(savedFilters.statusesApplied);
      } else {
        setStatuses([]);
        setStatusesApplied([]);
      }
      if (savedFilters.selectedEmpFilterApplied) {
        setSelectedEmpFilter(savedFilters.selectedEmpFilterApplied);
        setSelectedEmpFilterApplied(savedFilters.selectedEmpFilterApplied);
      } else {
        setSelectedEmpFilter([]);
        setSelectedEmpFilterApplied([]);
      }
      if (savedFilters.selectedOfficeFilterApplied) {
        setSelectedOfficeFilter(savedFilters.selectedOfficeFilterApplied);
        setSelectedOfficeFilterApplied(
          savedFilters.selectedOfficeFilterApplied,
        );
      } else {
        setSelectedOfficeFilter([]);
        setSelectedOfficeFilterApplied([]);
      }
      if (savedFilters.sortBy) {
        setSortBy(savedFilters.sortBy);
      } else {
        setSortBy(sortingVariables.UserName);
      }
      if (savedFilters.isDescending !== undefined) {
        setIsDescending(savedFilters.isDescending);
      } else {
        setIsDescending(true);
      }
    }
  }, [activeTab]);

  useEffect(() => {
    const officeContext = getOfficeNavigationContext();
    if (!isInOfficeNavigationContext(officeContext) || !officeContext?.officeId) {
      return;
    }
    const officeId = Number(officeContext.officeId);
    setSelectedOfficeFilter([officeId]);
    setSelectedOfficeFilterApplied([officeId]);
  }, []);

  // Update sortBy default when tab changes (only if no saved filters)
  useEffect(() => {
    if (!loadFiltersFromStorage(activeTab)) {
      setSortBy(1);
    }
  }, [activeTab]);

  const handleApplyFilter = () => {
    const newStates = {
      startDateApplied:
        startDate && endDate ? moment(startDate).format("YYYY-MM-DD") : null,
      endDateApplied:
        endDate && startDate
          ? moment(endDate).format("YYYY-MM-DD")
          : null,
      statusesApplied: [...statuses],
      selectedEmpFilterApplied: [...selectedEmpFilter],
      selectedOfficeFilterApplied: [...selectedOfficeFilter],
      sortBy: sortBy,
      isDescending: isDescending,
    };

    // Apply all new states to applied states
    setStartDateApplied(newStates.startDateApplied);
    setEndDateApplied(newStates.endDateApplied);
    setStatusesApplied(newStates.statusesApplied);
    setSelectedEmpFilterApplied(newStates.selectedEmpFilterApplied);
    setSelectedOfficeFilterApplied(newStates.selectedOfficeFilterApplied);

    // Save to localStorage
    saveFiltersToStorage(newStates, activeTab);
  };

  const resetFilter = () => {
    // Reset all filter states
    setRadioChecked(false);
    setRadioCheckedApplied(false);
    setStartDate(null);
    setStartDateApplied(null);
    setEndDate(null);
    setEndDateApplied(null);
    setStatuses([]);
    setStatusesApplied([]);
    setSelectedEmpFilter([]);
    setSelectedEmpFilterApplied([]);
    setSelectedOfficeFilter([]);
    setSelectedOfficeFilterApplied([]);
    setSelectedRoles([]);
    setSearchValue("");
    setSearchValueApplied("");
    setSortBy(sortingVariables.UserName);
    setIsDescending(true);
    setResetSearchTrigger((prev) => prev + 1);
    // Clear localStorage
    clearFiltersFromStorage(activeTab);
  };
  useEffect(() => {
    if (effectiveOwnerId) {
      if (preservedRef.current === null) {
        preservedRef.current = effectiveOwnerId;
      } else if (preservedRef.current !== effectiveOwnerId) {
        resetFilter();
        preservedRef.current = effectiveOwnerId;
      }
    }
  }, [effectiveOwnerId]);
  // Wrapper for setSortBy that also saves to localStorage
  const handleSetSortByAndIsDescending = useCallback(
    (newSortBy, newIsDescending) => {
      setSortBy(newSortBy);
      setIsDescending(newIsDescending);
      const existingFilters = loadFiltersFromStorage(activeTab) || {};
      saveFiltersToStorage(
        {
          ...existingFilters,
          sortBy: newSortBy,
          isDescending: newIsDescending,
        },
        activeTab,
      );
    },
    [activeTab],
  );

  return {
    // Filter states
    startDate,
    setStartDate,
    endDate,
    setEndDate,
    statuses,
    setStatuses,
    selectedEmpFilter,
    setSelectedEmpFilter,
    selectedOfficeFilter,
    setSelectedOfficeFilter,
    selectedRoles,
    setSelectedRoles,
    searchValue,
    setSearchValue,
    radioChecked,
    setRadioChecked,
    setRadioCheckedApplied,
    radioCheckedApplied,
    isFilterApplied,
    searchValueApplied,
    setSearchValueApplied,
    // Applied filter states
    startDateApplied,
    endDateApplied,
    statusesApplied,
    selectedEmpFilterApplied,
    selectedOfficeFilterApplied,

    // Sorting
    sortBy,
    isDescending,
    handleSetSortByAndIsDescending,
    // Actions
    handleApplyFilter,
    resetFilter,
    resetSearchTrigger,
  };
};
